/**
 * Shift roster: the week as the plant's own schedule sheet draws it. A row per
 * line × shift, and per day the batches that shift runs, each with the full
 * names of the people on it (someone from another line or shift is outlined).
 * Moving people works as on the Roster: drag a name onto a batch, pick and
 * click, use the ⋯ menu on a person, or click the batch to edit its people.
 */
import { addDays, format, isWithinInterval } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import jobOutcomesApi from "../../../../../services/job-outcomes-api";
import { RosterAssignment } from "../../../../../services/roster-assignments-api";
import { useCrewData } from "../controller/use-crew-data";
import { waitForFill } from "../controller/use-fill-range";
import { useJobOutcomes } from "../controller/use-job-outcomes";
import { ManualMoves } from "../controller/use-manual-moves";
import { AvailabilityIndex } from "../utils/availability";
import { buildCrewBoard, CrewItem, linesWithPlanFirst } from "../utils/crew-board"; // eslint-disable-line sort-imports
import { outcomeMark } from "../utils/job-outcome";
import { peopleOnDay } from "../utils/people-day";
import { Timeline } from "../utils/timeline"; // eslint-disable-line sort-imports
import { eyebrowSx, lbb } from "../utils/tokens";
import { BatchCard } from "./crew-board/batch-card";
import { BatchEditor } from "./crew-board/batch-editor";
import { HOUR_PX, HourHeader, RowTrack, TRACK_PX } from "./crew-board/hour-track";
import { PeoplePanel } from "./crew-board/people-panel";
import { RescheduleDialog } from "./crew-board/reschedule-dialog";
import { FillRequest } from "./fill-picker";
import { WorkerDragItem } from "./manual-controls";
import { PinEndOnDay } from "./week-line-view";

const LINE_COL = 140;
const SHIFT_COL = 92;
const DAY_COL_WEEK = 196;
const isDay = (label: string) => /1st|day|first|morning/i.test(label) && !/night/i.test(label);

// ---------------------------------------------------------------- the board

export interface CrewBoardProps {
	timeline: Timeline;
	weekStart: Date;
	locationId: number;
	/** Week: the seven days; Day: one day, wide. */
	zoom: "week" | "day";
	focusDate?: Date;
	selected: number | null;
	onSelect: (id: number | null) => void;
	moves: ManualMoves;
	onFill: (request: FillRequest) => void;
	pinEnd: PinEndOnDay;
	/** The plan itself changed (Update schedule moved orders): reload the week. */
	onPlanChanged?: () => void | Promise<void>;
	/** The week's roster blocks and who is out, for the people list. */
	assignments?: RosterAssignment[];
	availability?: AvailabilityIndex;
}

const NO_BLOCKS: RosterAssignment[] = [];
const PANEL_KEY = "roster.crew.peoplePanel";
const readPanelPref = (): boolean => {
	try {
		return localStorage.getItem(PANEL_KEY) !== "hidden";
	} catch {
		return true;
	}
};

const CrewBoard: React.FC<CrewBoardProps> = ({ timeline, weekStart, locationId, zoom, focusDate, selected, onSelect, moves, onFill, pinEnd, onPlanChanged, assignments = NO_BLOCKS, availability }) => {
	const weekKey = format(weekStart, "yyyy-MM-dd");
	const [editing, setEditing] = useState<{ item: CrewItem; workstation: string } | null>(null);
	const { shifts: lineShifts, crews } = useCrewData(locationId, weekStart);

	// The people list: one day of the week (the day shown, else today, else Monday); a picked person goes on the next batch clicked.
	const [showPeople, setShowPeople] = useState(readPanelPref);
	const [panelDay, setPanelDay] = useState<Date | null>(null);
	const [picked, setPicked] = useState<WorkerDragItem | null>(null);
	const weekEnd = addDays(weekStart, 6);
	const today = new Date();
	const defaultDay = focusDate && zoom === "day" ? focusDate : isWithinInterval(today, { start: weekStart, end: addDays(weekStart, 7) }) ? today : weekStart;
	const peopleDay = zoom === "day" && focusDate ? focusDate : panelDay && panelDay >= weekStart && panelDay <= addDays(weekEnd, 1) ? panelDay : defaultDay;
	const people = useMemo(
		() =>
			peopleOnDay(
				crews,
				assignments,
				peopleDay,
				(workerId, shiftId, dayIso) => Boolean(availability && (availability.isOut(workerId, shiftId) || availability.isOut(workerId, dayIso))),
				new Set(timeline.lanes.map((lane) => lane.workcenter.toLowerCase()))
			),
		[crews, assignments, peopleDay, availability, timeline]
	);
	const togglePeople = useCallback((show: boolean) => {
		setShowPeople(show);
		try {
			localStorage.setItem(PANEL_KEY, show ? "shown" : "hidden");
		} catch {
			// The preference is a convenience; the list still works without it.
		}
	}, []);
	const hidePeople = useCallback(() => togglePeople(false), [togglePeople]);
	const openPeople = useCallback(() => togglePeople(true), [togglePeople]);
	const handlePlaced = useCallback(() => setPicked(null), []);
	const outcomes = useJobOutcomes(weekStart);
	const markOf = useCallback(
		(item: CrewItem) => {
			const { byOrder, continues } = outcomes.index;
			const follows = continues.get(item.step.workOrderId);
			return outcomeMark(byOrder.get(item.step.workOrderId), follows === undefined ? undefined : byOrder.get(follows));
		},
		[outcomes.index]
	);
	// "Update schedule": the preview dialog for one late order, and undo from its card.
	const [updating, setUpdating] = useState<number | null>(null);
	const [undoing, setUndoing] = useState(false);
	const openUpdate = useCallback((workOrderId: number) => {
		setEditing(null);
		setUpdating(workOrderId);
	}, []);
	const closeUpdate = useCallback(() => setUpdating(null), []);
	const afterPlanChange = useCallback(() => {
		setUpdating(null);
		outcomes.reload();
		void onPlanChanged?.();
	}, [outcomes, onPlanChanged]);
	const undoUpdate = useCallback(
		(moveBatchId: string) => {
			setEditing(null);
			setUndoing(true);
			void jobOutcomesApi
				.undo(moveBatchId)
				.then((result) => (result.fillJobId ? waitForFill(result.fillJobId) : null))
				.catch(() => null)
				.finally(() => {
					setUndoing(false);
					afterPlanChange();
				});
		},
		[afterPlanChange]
	);

	const lines = useMemo(() => buildCrewBoard(timeline, lineShifts, crews, weekStart, 7), [timeline, lineShifts, crews, weekStart]);
	const dayIndexes = useMemo(() => {
		if (zoom === "day" && focusDate) {
			const d = Math.floor((new Date(focusDate.getFullYear(), focusDate.getMonth(), focusDate.getDate()).getTime() - weekStart.getTime()) / 86400000);
			return d >= 0 && d < 7 ? [d] : [0];
		}
		return [0, 1, 2, 3, 4, 5, 6];
	}, [zoom, focusDate, weekStart]);
	// Lines that run on the days shown come first; idle lines go to the bottom.
	const shownLines = useMemo(() => linesWithPlanFirst(lines, dayIndexes), [lines, dayIndexes]);
	const isDayZoom = zoom === "day";
	const columns = isDayZoom
		? `${LINE_COL}px ${SHIFT_COL}px ${TRACK_PX}px`
		: `${LINE_COL}px ${SHIFT_COL}px repeat(${dayIndexes.length}, minmax(${DAY_COL_WEEK}px, 1fr))`;
	// Day view opens on the chosen day's early morning; scroll on for its hours, or on into the next days.
	const scrollRef = useRef<HTMLDivElement>(null);
	const focusDay = dayIndexes[0] ?? 0;
	useEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollLeft = isDayZoom ? (focusDay * 24 + 6) * HOUR_PX : 0;
	}, [isDayZoom, focusDay, weekKey]);
	const pinEndFor = useCallback((workstation: string) => (workerId: string, day: string) => pinEnd(workerId, workstation, day), [pinEnd]);
	const openEditor = useCallback((item: CrewItem, workstation: string) => setEditing({ item, workstation }), []);
	const closeEditor = useCallback(() => setEditing(null), []);
	const clearSelection = useCallback((event: React.KeyboardEvent) => {
		if (event.key === "Escape") {
			onSelect(null);
			setPicked(null);
		}
	}, [onSelect]);

	const sticky = (left: number): React.CSSProperties => ({ position: "sticky", left, zIndex: 2, background: lbb.paper });
	const cellBorder: React.CSSProperties = { borderRight: lbb.borderHair, borderBottom: lbb.borderHair };

	return (
		<div style={{ display: "flex", border: lbb.borderHair, background: lbb.paper }}>
		<div ref={scrollRef} onKeyDown={clearSelection} style={{ overflow: "auto", flex: 1, minWidth: 0, background: lbb.paper, maxHeight: "calc(100vh - 225px)" }}>
			<div style={{ display: "grid", gridTemplateColumns: columns, minWidth: "fit-content" }}>
				{/* Header */}
				{["Work center", "Shift"].map((title, i) => (
					<div key={title} style={{ ...cellBorder, ...sticky([0, LINE_COL][i]), top: 0, zIndex: 4, padding: "10px 10px", ...eyebrowSx, fontSize: 10, color: lbb.ink60, borderBottom: lbb.borderInk }}>
						{title}
					</div>
				))}
				{isDayZoom && <HourHeader weekStart={weekStart} />}
				{!isDayZoom && dayIndexes.map((d) => {
					const date = addDays(weekStart, d);
					return (
						<div key={d} style={{ ...cellBorder, position: "sticky", top: 0, zIndex: 3, background: lbb.paper, padding: "8px 10px", borderBottom: lbb.borderInk }}>
							<div style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>{format(date, "EEE")}</div>
							<div style={{ fontFamily: lbb.fontBrand, fontSize: 15, fontWeight: 700, color: lbb.navy }}>{format(date, "d MMM")}</div>
						</div>
					);
				})}

				{shownLines.map((line) => (
					<React.Fragment key={line.workcenter}>
						<div
							style={{
								...cellBorder,
								...sticky(0),
								gridRow: `span ${Math.max(1, line.rows.length)}`,
								padding: "10px 10px",
								fontFamily: lbb.fontBrand,
								fontSize: 14,
								fontWeight: 700,
								color: lbb.navy,
								borderBottom: lbb.borderInk,
							}}
						>
							{line.workcenter}
						</div>
						{line.rows.map((row, r) => {
							const lastRow = r === line.rows.length - 1;
							const rowBorder = { ...cellBorder, ...(lastRow ? { borderBottom: lbb.borderInk } : {}) };
							return (
								<React.Fragment key={row.key}>
									<div style={{ ...rowBorder, ...sticky(LINE_COL), padding: "10px 8px" }}>
										<div style={{ fontFamily: lbb.fontUi, fontSize: 13, fontWeight: 700, color: lbb.navy, whiteSpace: "nowrap" }}>
											<span style={{ color: isDay(row.shift) ? lbb.warning : lbb.ink60, marginRight: 4 }}>{isDay(row.shift) ? "☀" : "☾"}</span>
											{row.shift}
										</div>
										<div style={{ fontFamily: lbb.fontMono, fontSize: 10.5, color: lbb.ink60, marginTop: 2 }}>{row.hours}</div>
									</div>
									{isDayZoom && (
										<RowTrack
											row={row}
											border={rowBorder}
											render={(item, style) => (
												<div key={item.key} style={style}>
													<BatchCard
														item={item}
														weekStart={weekStart}
														workstation={line.workcenter}
														moves={moves}
														selected={selected}
														onSelect={onSelect}
														onFill={onFill}
														pinEndFor={pinEndFor(line.workcenter)}
														onEdit={openEditor}
														mark={markOf(item)}
														onUpdateSchedule={openUpdate}
picked={picked}
onPersonPlaced={handlePlaced}
													/>
												</div>
											)}
										/>
									)}
									{!isDayZoom && dayIndexes.map((d) => (
										<div
											key={d}
											style={{
												...rowBorder,
												padding: 6,
												display: "flex",
												flexDirection: "column",
												gap: 6,
												background: row.windows[d] ? lbb.paper : lbb.alloy,
												minHeight: 56,
											}}
										>
											{row.days[d].map((item) => (
												<BatchCard
													key={item.key}
													item={item}
													weekStart={weekStart}
													workstation={line.workcenter}
													moves={moves}
													selected={selected}
													onSelect={onSelect}
													onFill={onFill}
													pinEndFor={pinEndFor(line.workcenter)}
													onEdit={openEditor}
													mark={markOf(item)}
													onUpdateSchedule={openUpdate}
picked={picked}
onPersonPlaced={handlePlaced}
												/>
											))}
											{!row.windows[d] && row.days[d].length === 0 && (
												<span style={{ fontFamily: lbb.fontUi, fontSize: 11, color: lbb.navy40 }}>No shift</span>
											)}
										</div>
									))}
								</React.Fragment>
							);
						})}
					</React.Fragment>
				))}
			</div>
			{editing && (
				<BatchEditor
					item={editing.item}
					workstation={editing.workstation}
					weekStart={weekStart}
					crews={crews}
					moves={moves}
					onFill={onFill}
					onClose={closeEditor}
					outcomes={outcomes}
					onUpdateSchedule={openUpdate}
					onUndoUpdate={undoUpdate}
				/>
			)}
			{updating !== null && <RescheduleDialog workOrderId={updating} onClose={closeUpdate} onApplied={afterPlanChange} />}
			{undoing && (
				<div style={{ position: "sticky", bottom: 0, left: 0, padding: "8px 12px", background: lbb.paper, borderTop: lbb.borderInk, fontFamily: lbb.fontUi, fontSize: 13, color: lbb.navy }}>
					Undoing the update and refilling the roster…
				</div>
			)}
		</div>
		{showPeople ? (
			<PeoplePanel
				people={people}
				weekStart={weekStart}
				day={peopleDay}
				onDay={zoom === "week" ? setPanelDay : undefined}
				picked={picked}
				onPick={setPicked}
				onHide={hidePeople}
			/>
		) : (
			<button
				type="button"
				onClick={openPeople}
				style={{ writingMode: "vertical-rl", border: "none", borderLeft: lbb.borderHair, background: lbb.paper, color: lbb.navy, fontFamily: lbb.fontUi, fontSize: 12, fontWeight: 600, padding: "10px 4px", cursor: "pointer" }}
			>
				People
			</button>
		)}
		</div>
	);
};

export default CrewBoard;
